import React from 'react';
import { useWorkflow } from '../context/WorkflowContext';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiMapPin, FiBriefcase, FiZap, FiActivity, FiGlobe } = FiIcons;

const SearchPage = () => {
  const { 
    companyName, setCompanyName, 
    location, setLocation, 
    setPhase, provider, selectedModel, apiKey
  } = useWorkflow();

  const isConfigured = !!apiKey;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (companyName.trim() && isConfigured) setPhase(2);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-20">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-sonar-green/10 text-sonar-green px-4 py-1.5 rounded-full text-[10px] font-black mb-6 border border-sonar-green/20 tracking-[0.2em] uppercase">
          <SafeIcon icon={FiActivity} className={isConfigured ? 'animate-pulse' : ''} />
          {isConfigured ? `${provider.toUpperCase()} Engine Active` : 'Engine Offline'}
        </div>
        <h1 className="text-4xl md:text-5xl font-black text-sonar-text mb-4 tracking-tight uppercase">
          Target <span className="text-sonar-green">Resolution.</span>
        </h1>
        <p className="text-sonar-text-muted text-sm font-mono max-w-lg mx-auto">
          Input legal entity name and secondary location vector to initialize OMEN intelligence synthesis.
        </p>
      </div>

      <div className="bg-sonar-card rounded-3xl p-8 border border-sonar-border shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 radar-scan opacity-5 pointer-events-none" />
        
        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          <div className="space-y-4">
            <div className="relative group">
              <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-sonar-green/50 transition-colors group-focus-within:text-sonar-green" />
              <input 
                type="text" 
                required
                className="w-full pl-12 pr-4 py-4.5 border border-sonar-border rounded-2xl bg-sonar-bg text-sonar-text outline-none focus:border-sonar-green transition-all font-mono placeholder:text-slate-600 shadow-inner"
                placeholder="Full Legal Name (e.g. Tesla Inc)"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
              />
            </div>
            
            <div className="relative group">
              <SafeIcon icon={FiMapPin} className="absolute left-4 top-1/2 -translate-y-1/2 text-sonar-green/50 transition-colors group-focus-within:text-sonar-green" />
              <input 
                type="text" 
                className="w-full pl-12 pr-4 py-4.5 border border-sonar-border rounded-2xl bg-sonar-bg text-sonar-text outline-none focus:border-sonar-green transition-all font-mono placeholder:text-slate-600 shadow-inner"
                placeholder="State / Country / HQ City (Optional)"
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>
          </div>

          {!isConfigured && (
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-amber-200/80 font-mono leading-relaxed">
                <strong className="text-amber-400 block uppercase mb-0.5">Configuration Required</strong>
                No intelligence engine linked. Navigate to <strong>Settings</strong> to connect your API credentials.
              </p>
            </div>
          )}

          <button 
            type="submit" 
            disabled={!isConfigured || !companyName.trim()}
            className="w-full bg-sonar-green text-black py-4.5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs disabled:opacity-20 hover:bg-sonar-green-glow transition-all shadow-[0_0_30px_rgba(46,204,113,0.2)] flex items-center justify-center gap-3"
          >
            {isConfigured ? (
              <>
                <SafeIcon icon={FiGlobe} />
                Resolve Target Entity
              </>
            ) : 'Engine Offline'}
          </button>
        </form>
      </div>
      
      <div className="mt-12 grid grid-cols-3 gap-4">
        {[
          { label: 'OSINT Nodes', val: '200+', icon: FiIcons.FiServer },
          { label: 'Avg Latency', val: '4.2s', icon: FiIcons.FiZap },
          { label: 'Accuracy', val: '99.8%', icon: FiIcons.FiShield },
        ].map((stat, i) => (
          <div key={i} className="text-center p-4">
            <div className={`w-8 h-8 mx-auto rounded-full bg-sonar-green/5 border border-sonar-green/20 flex items-center justify-center text-sonar-green mb-3`}>
              <SafeIcon icon={stat.icon} className="text-xs" />
            </div>
            <div className="text-lg font-black text-sonar-text font-mono leading-none">{stat.val}</div>
            <div className="text-[9px] font-bold text-sonar-text-muted uppercase tracking-widest mt-1">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchPage;